import { useEffect, useState } from "react";
import { approveRefund } from "../../services/order";
import { toast } from "sonner";
import { Button, Input, Pagination, Table, Typography } from "antd";
import { capitalizeFirstLetter } from "../../utils/global-function";
import {
  regenerateCoupon,
  reissueCoupons,
} from "../../services/reissue-coupons";

const { Title } = Typography;

const ReissueCoupons = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [buttonLoader, setButtonLoader] = useState({
    status: false,
    id: null,
  }); // Button loader state
  const [totalItem, setTotalItem] = useState(0);

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      render: (_, __, index) => apiPayload.offset + index + 1,
    },
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: "Customer Name",
      dataIndex: "billing_name",
      key: "billing_name",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: "City",
      dataIndex: "shipping_city",
      key: "shipping_city",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },

    {
      title: "State",
      dataIndex: "shipping_province",
      key: "shipping_province",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: "Coupon Code",
      dataIndex: "coupon_code",
      key: "coupon_code",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
    },
    {
      title: "Coupon Status",
      dataIndex: "coupon_status",
      key: "coupon_status",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
      render: (val, record) => <div>{capitalizeFirstLetter(val)}</div>,
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      key: "order_status",
      onHeaderCell: () => ({
        style: { whiteSpace: "nowrap" },
      }),
      render: (val, record) => <div>{capitalizeFirstLetter(val)}</div>,
    },

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) =>
        record.coupon_status === "pending" ? (
          <Button
            type="primary"
            loading={
              buttonLoader.status && buttonLoader.id == record.coupon_code
            }
            onClick={() =>
              regenerateCouponHandler(record?.order_number, record?.coupon_code)
            }
            className="px-2 py-1 bg-[#1B5C29] hover:bg-[#13471e] rounded shadow text-white"
          >
            Regenerate Coupon
          </Button>
        ) : (
          <Button type="dashed" disabled>
            Already Regenerate
          </Button>
        ),
    },
  ];

  const [apiPayload, setApiPayload] = useState({
    limit: 10,
    offset: 0,
    search: "",
  });

  const fetchRefunds = async (limit = 10, offset = 0, search) => {
    try {
      setLoading(true);
      const response = await reissueCoupons({
        limit,
        offset,
        search: search || undefined, // only send if available
      });
      const { data, status } = response || {};
      if (status === 200 || status === 201) {
        setData(data?.orders?.data);
        setTotalItem(Number(data?.orders?.total));
      }
    } catch (error) {
      toast.error("Failed to fetch refund data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (val) => {
    setApiPayload({ limit: 10, offset: 0, search: val });
    fetchRefunds(10, 0, val);
  };

  const regenerateCouponHandler = async (orderNumber, couponCode) => {
    setButtonLoader({
      id: couponCode,
      status: true,
    });
    try {
      const res = await regenerateCoupon({
        orderNumber,
        couponCode,
      });
      const { data, status } = res || {};
      if (status === 200) {
        toast.success(data?.message);
        await fetchRefunds(
          apiPayload.limit,
          apiPayload.offset,
          apiPayload.search
        );
      } else {
        toast.error(data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to process the refund");
    } finally {
      setButtonLoader({
        id: null,
        status: false,
      });
    }
  };

  const paginationHandler = (page, limit) => {
    const offset = (page - 1) * limit;
    fetchRefunds(limit, offset, apiPayload.search);
    setApiPayload((pre) => ({
      ...pre,
      limit: limit,
      offset: offset,
    }));
  };

  useEffect(() => {
    fetchRefunds(10, 0, "");
  }, []);

  return (
    <>
      <main className="w-full pb-6">
        <h1 className="text-2xl mb-6 font-semibold text-gray-900 whitespace-nowrap">
          Reissue Coupons
        </h1>
        <div className="bg-[#619269] text-white p-4 mb-6 rounded-t-lg">
          <div className="flex items-center gap-4">
            <span className="font-medium">SEARCH</span>
            <Input
              placeholder="Search by Order Number, Email, Phone"
              value={apiPayload.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-96"
              size="large"
            />
          </div>
        </div>

        <div className="bg-white rounded-b-lg shadow-sm">
          <div className="p-4 border-b">
            <Title level={4} className="m-0 text-gray-700">
              Reissue Coupons List
            </Title>
          </div>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1400 }}
            pagination={false}
          />
          <div className="flex justify-end py-4 pr-2">
            <Pagination
              total={totalItem}
              pageSize={apiPayload.limit}
              showQuickJumper
              showSizeChanger={true}
              onChange={paginationHandler}
            />
          </div>
        </div>
      </main>
    </>
  );
};

export default ReissueCoupons;
